/**
 * Документы — копия legacy main_docs.tpl + рекурсивного main_docs_inner.tpl.
 * Дерево = вложенные bootstrap-аккордеоны (.panel-group > .panel > .panel-heading +
 * .panel-collapse), файлы — .panel с ссылкой a.doc-file (иконка из main.css).
 * Legacy Main::docs дополнительно подключал /assets/css/docs.min.css — делаем так же.
 *
 * Раскрытие папок в legacy делал bootstrap.js (data-toggle="collapse"); в SPA
 * jQuery/bootstrap.js нет, поэтому тот же класс `in` переключается состоянием React —
 * разметка, классы и aria-атрибуты остаются прежними.
 *
 * Скачивание — по легаси-URL /docs/get/{id}/{filename}.
 */

import { useState } from 'react'

import { api } from '@/api/client'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Узел дерева от /api/content/docs. У корня id='0', top_folder='00' (legacy get_docs). */
interface DocsNode {
  id: number | string
  name: string
  filename: string
  top_folder: number | string
  folders?: DocsNode[]
  files?: DocsNode[]
}

export function DocsPage() {
  useLegacyCss('docs.min')
  const { data } = useApi<DocsNode>('/api/content/docs')

  return (
    <div>
      {data !== null && <DocsInner docdata={data} />}

      {data !== null && (data.folders ?? []).length === 0 && (
        <div className="row">
          <div className="col-12">
            <center>Нет данных</center>
          </div>
        </div>
      )}
    </div>
  )
}

/** main_docs_inner.tpl: у корня (id == 0) нет заголовка, его тело раскрыто (класс `in`). */
function DocsInner({ docdata }: { docdata: DocsNode }) {
  const isRoot = Number(docdata.id) === 0
  const [open, setOpen] = useState(isRoot)

  const body = (
    <div
      id={`collapse_${docdata.id}`}
      className={`panel-collapse collapse${open ? ' in' : ''}`}
      role="tabpanel"
      aria-labelledby={`heading_${docdata.id}`}
    >
      <div className="panel-body">
        {(docdata.folders ?? []).map((folder) => (
          <DocsInner key={folder.id} docdata={folder} />
        ))}
        {(docdata.files ?? []).map((file) => (
          <div className="panel panel-default" key={file.id}>
            <div className="panel-heading min-padding">
              <table>
                <tbody>
                  <tr>
                    <a
                      className="doc-file"
                      href={`/docs/get/${file.id}/${file.filename}`}
                      title={file.filename}
                      // Файл закрыт авторизацией, а по обычной ссылке браузер не пошлёт
                      // заголовок Authorization — качаем сами и отдаём blob (api/client.ts).
                      onClick={(event) => {
                        event.preventDefault()
                        void api.downloadFile(
                          `/docs/get/${file.id}/${file.filename}`,
                          file.filename ?? file.name,
                        )
                      }}
                    >
                      {file.name}
                    </a>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  if (isRoot) return body

  return (
    <div
      className="panel-group"
      id={`accordion_${docdata.top_folder}`}
      role="tablist"
      aria-multiselectable="true"
    >
      <div className="panel panel-default">
        <div className="panel-heading min-padding" role="tab" id={`heading_${docdata.id}`}>
          <h4 className="panel-title">
            <a
              role="button"
              data-toggle="collapse"
              data-parent={`#accordion_${docdata.top_folder}`}
              href={`#collapse_${docdata.id}`}
              aria-expanded={open}
              aria-controls={`collapse_${docdata.id}`}
              onClick={(event) => {
                event.preventDefault()
                setOpen((value) => !value)
              }}
            >
              {docdata.name}
            </a>
          </h4>
        </div>
        {body}
      </div>
    </div>
  )
}
